<template>
  <loading-page
    v-if="loading"
    full
  />
  <div
    v-else-if="showIntro"
  >
    <live-hubs-intro>
      <custom-alert
        icon-name="Info"
        items-center
        class="mt-2"
      >
        {{ errorMessage }}
      </custom-alert>
    </live-hubs-intro>
  </div>
  <div
    v-else
    class="table-wrapper"
  >
    <div class="d-flex justify-content-between flex-wrap">
      <div class="hubs-main d-flex w-100">
        <b-button
          variant="bg-primary"
          :to="{ name: 'create-live-hub' }"
        >
          <GjIcon
            class="icon-left"
            name="Plus"
          />
          Create Live Hub
        </b-button>
        <b-button
          variant="bg-white"
          class="d-none d-lg-flex"
          :disabled="!isSelected"
          @click="idClick(selected[0].name)"
        >
          Edit
        </b-button>
        <b-button
          variant="bg-white"
          class="d-none d-lg-flex"
          :disabled="!isSelected"
          @click="onSettingsClick(selected[0].name)"
        >
          Settings
        </b-button>
        <button-loading
          v-if="$can('delete', 'hubs', 'encoder')"
          variant="bg-white"
          :disabled="!isSelected || removingLiveHub || selected[0].status === 'Deleted'"
          :spinning="removingLiveHub"
          class="d-none d-lg-inline action-live-hub-button"
          @click="onDeleteClick(selected[0].name, selected[0].livestreamProvider)"
        >
          Delete
        </button-loading>
        <b-button
          variant="bg-white"
          class="ml-auto mr-0"
          :disabled="fetchingLiveHubs"
          @click="onGetLiveHubs()"
        >
          <GjIcon
            class="icon-left"
            name="Refresh"
          />
          Refresh
        </b-button>
      </div>
    </div>

    <div
      :class="
        currentBreakPoint === 'lg' || currentBreakPoint === 'xl'
          ? 'default-table'
          : 'mt-2'
      "
    >
      <div class="position-relative d-none d-lg-block">
        <b-row>
          <b-col
            v-if="!fetchingLiveHubStatuses"
            class="pt-2 ml-2"
          >
            <b-dropdown
              id="hubs-filter-dropdown-1"
              :text="liveHubStatusText"
              variant="bg-white"
              class="dropdown-icon-wrapper"
            >
              <template #button-content>
                <span>{{ liveHubStatusText }}</span>
                <GjIcon
                  class="icon-right"
                  name="ArrowDown"
                />
              </template>
              <b-dropdown-item
                v-for="status in liveHubsStatusOptions"
                :key="status.id"
                @click="liveHubStatus = status.id"
              >
                {{ status.value }}
              </b-dropdown-item>
            </b-dropdown>
          </b-col>

          <b-col>
            <b-input-group
              v-b-tooltip.hover.v-primary
              class="searchInputGroup ml-auto mr-2 pt-2 mt-sm-0"
              title="Search by name or description"
            >
              <b-input-group-prepend is-text>
                <b-icon-search @click="searchInputChange" />
              </b-input-group-prepend>
              <b-form-input
                id="filterInput"
                v-model.trim="searchInput"
                type="search"
                class="searchInput"
                placeholder="Search..."
                @change="searchInputChange"
              />
            </b-input-group>
          </b-col>
        </b-row>

        <b-table
          ref="table"
          select-mode="single"
          :items="liveHubs"
          :fields="fields"
          :class="{ loaded: !loading }"
          show-empty
          selectable
          responsive
          @row-selected="onRowSelected"
        >
          <template #cell(selected)="{ rowSelected }">
            <select-button :selected="rowSelected" />
          </template>
          <template #empty>
            <div
              v-if="loading"
              class="loading-table text-center py-3"
            >
              <b-spinner variant="primary" />
            </div>
            <no-data
              v-else
              no-data-text="No live hubs have been added yet..."
              create-text="+ Create a live hub"
              access-page="Live Hubs"
              icon="Section"
              :has-permissions="hasPermissions"
              @action="$router.push({ name: 'create-live-hub' })"
            />
          </template>
          <template #cell(name)="data">
            <router-link
              :to="{
                name: 'live-hub-settings',
                params: { liveHubName: data.value },
              }"
            >
              {{ data.value }}
            </router-link>
          </template>
          <template #cell(insertDate)="data">
            {{ formatDateTime(data.value) }}
          </template>
          <template #cell(startDateTime)="data">
            {{ formatDateTime(data.value) }}
          </template>
          <template #cell(endDateTime)="data">
            {{ formatDateTime(data.value) }}
          </template>
        </b-table>
        <table-busy v-if="loading && liveHubs.length > 0" />
      </div>

      <!-- MOBILE -->
      <div class="position-relative d-lg-none mt-1">
        <div :class="{ 'opacity-7': loading }">
          <template v-for="item in liveHubs">
            <card
              :key="item.name"
              :fields="fields"
              :item="item"
              class="top-fade-in"
            >
              <template #options>
                <b-dropdown
                  id="dropdown-1"
                  right
                  variant="white"
                >
                  <template #button-content>
                    <GjIcon name="Moreoptions" />
                  </template>
                  <b-dropdown-item @click="idClick(item.name)">
                    Edit Hub
                  </b-dropdown-item>
                </b-dropdown>
              </template>

              <template #name>
                <router-link
                  :to="{
                    name: 'live-hub-settings',
                    params: { liveHubName: item.name },
                  }"
                >
                  {{ item.name }}
                </router-link>
              </template>

              <template #insertDate>
                {{ formatDateTime(item.insertDate) }}
              </template>
              <template #startDateTime>
                {{ formatDateTime(item.startDateTime) }}
              </template>
              <template #endDateTime>
                {{ formatDateTime(item.endDateTime) }}
              </template>
            </card>
          </template>
        </div>
        <div
          v-if="loading"
          class="mobile-loading"
        >
          <table-busy />
        </div>
        <template v-if="!loading && liveHubs.length < 1">
          <paper-simple class="top-fade-in">
            <no-data
              class="text-center"
              no-data-text="No live hubs have been added yet..."
              create-text="+ Create a live hub"
              access-page="Live Hubs"
              icon="Section"
              :has-permissions="hasPermissions"
              @action="$router.push({ name: 'create-live-hub' })"
            />
          </paper-simple>
        </template>
      </div>

      <div
        v-if="liveHubs.length > 0"
        class="default-pagination"
      >
        <b-pagination
          v-model="currentPage"
          :total-rows="totalCount"
          :per-page="pageSize"
          first-number
          last-number
        />
        <div class="results">
          Viewing {{ first.toLocaleString() }} to {{ last.toLocaleString() }} of {{ totalCount.toLocaleString() }} Items
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Card from '@/components/card/Card.vue'
import LiveHubsIntro from './LiveHubsIntro.vue'
import { BIconSearch } from 'bootstrap-vue'

export default {
  name: 'LiveHubs',
  components: {
    Card,
    BIconSearch,
    LiveHubsIntro,
  },
  data() {
    return {
      searchInput: '',
      liveHubStatus: -1,
      fields: [
        {
          key: 'selected',
          label: '',
          variant: 'select',
          hide: true,
        },
        {
          key: 'name',
          variant: 'guid',
          isPrimary: true,
        },
        { key: 'status', variant: 'small', isPrimary: true },
        { key: 'insertDate', variant: 'time' },
        { key: 'startDateTime', label: 'Start Date', variant: 'time' },
        { key: 'endDateTime', label: 'End Date', variant: 'time' },
        { key: 'description', label: 'Description' },
      ],
      selected: [],
      hasPermissions: true,
      showIntro: false,
      errorMessage: '',
    }
  },
  computed: {
    fetchingLiveHubs() {
      return this.$store.state.liveHubs.fetchingLiveHubs
    },
    fetchingLiveHubStatuses() {
      return this.$store.state.liveHubs.fetchingLiveHubsStatuses
    },
    removingLiveHub() {
      return this.$store.state.liveHubs.removingLiveHub
    },
    liveHubsStatusOptions() {
      return this.$store.state.liveHubs.liveHubStatuses || []
    },
    liveHubStatusText() {
      return (
        this.liveHubsStatusOptions.find(h => h.id === this.liveHubStatus)
          ?.value || ''
      )
    },
    isSelected() {
      if (this.selected[0]) {
        return true
      }
      return false
    },
    currentBreakPoint() {
      return this.$store.getters['app/currentBreakPoint']
    },
    loading() {
      return this.$store.state.liveHubs.fetchingLiveHubs
    },
    liveHubs() {
      return this.$store.state.liveHubs.liveHubs
    },
    totalCount() {
      return this.resultInfo.totalCount
    },
    resultInfo() {
      return this.$store.state.liveHubs.liveHubsResultInfo
    },
    pageSize() {
      return this.resultInfo.pageSize || 10
    },
    pageIndex() {
      return this.currentPage - 1
    },
    first() {
      return this.pageIndex * this.pageSize + 1
    },

    last() {
      return this.currentPage * this.pageSize < this.totalCount
        ? this.currentPage * this.pageSize
        : this.totalCount
    },
    currentPage: {
      get() {
        return this.$store.getters['liveHubs/currentPage']
      },
      set(val) {
        this.$store.commit('liveHubs/SET_CURRENT_PAGE', val)
      },
    },
  },
  watch: {
    currentPage() {
      this.onGetLiveHubs()
    },
    liveHubStatus() {
      if (this.currentPage === 1) {
        this.onGetLiveHubs()
      } else {
        this.currentPage = 1
      }
    },
  },
  created() {
    this.onGetLiveHubs()
    this.fetchLiveHubsStatuses()
  },
  methods: {
    searchInputChange() {
      if (this.currentPage === 1) {
        this.onGetLiveHubs()
      } else {
        this.currentPage = 1
      }
    },
    onRowSelected(items) {
      this.selected = items
    },
    onGetLiveHubs() {
      this.$store
        .dispatch('liveHubs/fetchLiveHubsV2', {
          pageIndex: this.pageIndex,
          pageSize: this.pageSize,
          status: this.liveHubStatus,
          search: this.searchInput,
        })
        .catch(error => {
          if (error.response?.status === 403) {
            this.hasPermissions = false
            return
          }
          if (error.response?.status === 404) {
            this.showIntro = true
            this.errorMessage = error.response?.data?.errors[0]
            return
          }
          this.errorToast(error.response?.data?.errors[0] || 'Something went wrong while fetching live hubs.')
        })
    },
    onSettingsClick(liveHubName) {
      this.$router.push({ name: 'live-hub-settings', params: { liveHubName } })
    },
    idClick(liveHubName) {
      this.$router.push({ name: 'edit-live-hub', params: { liveHubName } })
    },
    onDeleteClick(liveHubId, livestreamProvider) {
      const version = livestreamProvider === 1 ? '' : 'V2'
      this.confirmDelete(
        'Delete Live Hub',
        'Are you sure you want to delete this live hub?',
      ).then(ok => {
        if (ok) {
          this.$store
            .dispatch('liveHubs/removeLiveHub', { liveHubId, version })
            .then(() => {
              this.successToast('Live Hub was removed')
              this.selected = []
              if (this.liveHubs.length === 0 && this.currentPage > 1) {
                this.currentPage = 1
              } else {
                this.onGetLiveHubs()
              }
            })
            .catch(error => {
              this.errorToast(error.response?.data?.errors[0] || 'Something went wrong while deleting live hub.')
            })
        }
      })
    },
    fetchLiveHubsStatuses() {
      this.$store.dispatch('liveHubs/fetchLiveHubsStatuses').catch(() => {
        this.errorToast('Something went wrong while fetching statuses.')
      })
    },
  },
}
</script>
<style scoped>
.searchInputGroup {
  width: 250px;
}
.searchInput {
  margin-right: 0;
}
</style>
